import uniq from 'lodash/uniq';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import LazyLoad from 'react-lazyload';

import DateInput from '@/components/common/ui/DateInput';
import SearchHeader, { IHeaderMenu } from '@/components/ui/SearchHeader/SearchHeader';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import getSeason from '@/utility/getSeason';

import css from './Hero.module.css';

export const DEFAULT_IMAGE =
  'https://d1o5877uy6tsnd.cloudfront.net/marketplace-search-production/img/en-us/homepage/hero-25846d08b7207d696c43e6f9e6a7e3ab.jpg';

type TSeason = 'winter' | 'fall' | 'spring' | 'summer';

export type TImage = { season: TSeason; url: string };

const findImageBySeason = (season: TSeason, images: TImage[]) => {
  const img = images.find(image => image.season === season);
  return img?.url ?? null;
};

const getImageUrlOfSeasonByDate = (date: Date, images: TImage[]) => {
  const season = getSeason(date);
  return season && findImageBySeason(season, images);
};

export const getImageURL = ({ images, date = new Date() }: { images?: TImage[]; date?: Date }) => {
  const seasons = uniq(images?.map(image => image.season));
  if (!seasons?.length || !images?.length) return DEFAULT_IMAGE;

  const imageURL = getImageUrlOfSeasonByDate(date, images) || images?.find(image => image.url)?.url;

  if (!imageURL || imageURL === '') {
    return DEFAULT_IMAGE;
  }

  return imageURL;
};

const Hero = ({
  title,
  images,
  lockedSearchCategory,
  onSubmit,
}: {
  title: string;
  images: TImage[];
  // preselect a rental category by default and don't render top menu so we can't change it
  lockedSearchCategory?: IHeaderMenu;
  onSubmit: (startDate?: Date, endDate?: Date) => void;
}) => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [imageURL, setImageURL] = useState(() => getImageURL({ images }));
  const useUSM = useExperimentIsEnabled(OptimizelyFlags.USM_FOR_GUIDE_PAGES);

  const changeDate = (startDate: Date | undefined, endDate: Date | undefined) => {
    setImageURL(getImageURL({ date: startDate }));
    setStartDate(startDate);
    setEndDate(endDate);
  };

  return useUSM ? (
    <div
      className={`flex flex-col items-center relative text-center justify-center w-full ${css.heroUSM}`}>
      <div className="absolute z-0 w-full h-full">
        <div className={css.heroBackgroundOverlay} />
        <div
          className="h-full bg-center bg-cover"
          style={{ backgroundImage: `url('${imageURL}')` }}
        />
      </div>

      <div className="relative flex flex-col w-full h-full max-w-lg px-4">
        <h1 className="mt-4 text-white">
          {title}
          <br />
          <FormattedMessage defaultMessage="RV Guide" id="+uctzp" />
        </h1>

        <div className="z-30 flex flex-col w-full my-4">
          <SearchHeader
            isSERP
            isUniversalSearch
            isFullUniversalSearch
            lockedCategory={lockedSearchCategory}
            onSubmit={onSubmit}
          />
        </div>
      </div>
    </div>
  ) : (
    <div className={css.hero}>
      <div className={css.heroBackground}>
        <div className={css.heroBackgroundOverlay} />
        <LazyLoad height={532}>
          <div
            className={css.heroBackgroundImage}
            style={{ backgroundImage: `url('${imageURL}')` }}
          />
        </LazyLoad>
      </div>

      <h1 className="text-white">
        {title}
        <br />
        <FormattedMessage defaultMessage="RV Guide" id="+uctzp" />
      </h1>

      <label htmlFor="dates" className={css.subheading}>
        <FormattedMessage defaultMessage="Share your visitation dates" id="gjn6S9" />
      </label>
      <div className={css.dateInput}>
        <DateInput onChange={changeDate} placeholder="Pick up / Drop off" />
        <button
          className="flex items-center px-4 ml-2 text-white bg-gray-850 rounded-md focus:ring focus:outline-none"
          onClick={() => {
            if (startDate && endDate) {
              onSubmit(startDate, endDate);
            }
          }}>
          <FormattedMessage defaultMessage="Search" id="xmcVZ0" />
        </button>
      </div>
    </div>
  );
};

export default Hero;
